import { useEffect, useRef } from 'react';

/**
 * Hook to check if the page is displaying properly on mobile.
 * If the page content isn't visible within 1.5 seconds, auto-reload.
 * This helps with mobile rendering issues.
 */
export function useMobileDisplayCheck(contentSelector = '[data-page-content]') {
  const hasChecked = useRef(false);
  const reloadAttempted = useRef(false);

  useEffect(() => {
    // Only run on mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
    if (!isMobile) return;
    
    // Only check once per page load
    if (hasChecked.current) return;
    hasChecked.current = true;
    
    // Check if already attempted reload to prevent infinite loop
    const reloadKey = `noyo_reload_${window.location.pathname}`;
    const lastReload = sessionStorage.getItem(reloadKey);
    if (lastReload && Date.now() - parseInt(lastReload) < 5000) {
      return; // Already reloaded recently, don't reload again
    }
    
    const checkDisplay = () => {
      // Check if main content is visible
      const content = document.querySelector(contentSelector);
      const pageTitle = document.querySelector('h1');
      
      // If there's no content and no title visible, something is wrong
      if (!content && !pageTitle) {
        // Check if body has any meaningful content
        const bodyText = document.body.innerText?.trim();
        if (!bodyText || bodyText.length < 50) {
          console.warn('Mobile display issue detected, reloading page...');
          sessionStorage.setItem(reloadKey, Date.now().toString());
          window.location.reload();
        }
      }
    };
    
    // Check after 1.5 seconds
    const timer = setTimeout(checkDisplay, 1500);
    
    return () => clearTimeout(timer);
  }, [contentSelector]);
}

export default useMobileDisplayCheck;
